import { useState } from 'react';
import { BackgroundImage } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import { convertToWebP } from '../../utils/imageConvert';

export function useBackgroundImageManagement(workspaceId: string | undefined) {
  const [backgroundImages, setBackgroundImages] = useState<BackgroundImage[]>([]);
  const [bgImageFiles, setBgImageFiles] = useState<File[]>([]);
  const [isUploadingBgImage, setIsUploadingBgImage] = useState(false);
  const [bgUploadProgress, setBgUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [bgUploadErrors, setBgUploadErrors] = useState<string[]>([]);
  const [bgImageUrl, setBgImageUrl] = useState('');
  const [bgImageName, setBgImageName] = useState('');
  const [isAddingBgImage, setIsAddingBgImage] = useState(false);
  const [isAddingSelectedAsBackgrounds, setIsAddingSelectedAsBackgrounds] = useState(false);

  const fetchBackgroundImages = async () => {
    try {
      const data = await apiClient.get<BackgroundImage[]>(`/api/workspaces/${workspaceId}/background-images`);
      setBackgroundImages(data);
    } catch (error) {
      console.error('Failed to fetch background images:', error);
    }
  };

  const handleBackgroundImageUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (bgImageFiles.length === 0) return;

    setIsUploadingBgImage(true);
    setBgUploadProgress({ current: 0, total: bgImageFiles.length });
    setBgUploadErrors([]);

    const uploadedImages: BackgroundImage[] = [];
    const errors: string[] = [];
    const BATCH_SIZE = 3;

    const uploadSingleFile = async (file: File): Promise<BackgroundImage> => {
      const webpFile = await convertToWebP(file);
      const formData = new FormData();
      formData.append('file', webpFile);
      return apiClient.upload<BackgroundImage>(`/api/workspaces/${workspaceId}/background-images/upload`, formData);
    };

    try {
      for (let i = 0; i < bgImageFiles.length; i += BATCH_SIZE) {
        const batch = bgImageFiles.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(batch.map((file) => uploadSingleFile(file)));

        results.forEach((result, idx) => {
          const file = batch[idx];
          if (result.status === 'fulfilled') {
            uploadedImages.push(result.value);
          } else {
            console.error(`Failed to upload ${file.name}:`, result.reason);
            errors.push(file.name);
          }
          setBgUploadProgress({ current: i + idx + 1, total: bgImageFiles.length });
        });
      }

      if (uploadedImages.length > 0) {
        setBackgroundImages([...backgroundImages, ...uploadedImages]);
      }
      if (errors.length > 0) {
        setBgUploadErrors(errors);
      }
      setBgImageFiles([]);
    } catch (error) {
      console.error('Failed to upload background images:', error);
    } finally {
      setIsUploadingBgImage(false);
      setBgUploadProgress(null);
    }
  };

  const handleDeleteBackgroundImage = async (imageId: string) => {
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/background-images/${imageId}`);
      setBackgroundImages(backgroundImages.filter((img) => img.id !== imageId));
    } catch (error) {
      console.error('Failed to delete background image:', error);
    }
  };

  const handleAddBackgroundImageUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bgImageUrl || !bgImageName) return;

    setIsAddingBgImage(true);
    try {
      const newImage = await apiClient.post<BackgroundImage>(`/api/workspaces/${workspaceId}/background-images`, {
        url: bgImageUrl,
        name: bgImageName,
      });
      setBackgroundImages([...backgroundImages, newImage]);
      setBgImageUrl('');
      setBgImageName('');
    } catch (error) {
      console.error('Failed to add background image:', error);
    } finally {
      setIsAddingBgImage(false);
    }
  };

  const handleAddSelectedAsBackgrounds = async (
    selectedImages: Set<string>,
    clearSelections: () => void,
  ) => {
    if (selectedImages.size === 0) return;

    setIsAddingSelectedAsBackgrounds(true);
    try {
      const imagesToAdd = Array.from(selectedImages);
      const newImages: BackgroundImage[] = [];

      for (let i = 0; i < imagesToAdd.length; i++) {
        const imageUrl = imagesToAdd[i];
        const urlParts = imageUrl.split('/');
        const fileName = urlParts[urlParts.length - 1].split('?')[0] || `Image ${i + 1}`;
        const imageName = fileName.length > 30 ? fileName.substring(0, 30) + '...' : fileName;

        try {
          const newImage = await apiClient.post<BackgroundImage>(`/api/workspaces/${workspaceId}/background-images`, {
            url: imageUrl,
            name: imageName,
          });
          newImages.push(newImage);
        } catch (err) {
          console.error(`Failed to add background image: ${imageUrl}`, err);
        }
      }

      if (newImages.length > 0) {
        setBackgroundImages([...backgroundImages, ...newImages]);
      }

      clearSelections();
    } catch (error) {
      console.error('Failed to add selected images as backgrounds:', error);
    } finally {
      setIsAddingSelectedAsBackgrounds(false);
    }
  };

  return {
    backgroundImages,
    bgImageFiles,
    setBgImageFiles,
    isUploadingBgImage,
    bgUploadProgress,
    bgUploadErrors,
    bgImageUrl,
    setBgImageUrl,
    bgImageName,
    setBgImageName,
    isAddingBgImage,
    isAddingSelectedAsBackgrounds,
    fetchBackgroundImages,
    handleBackgroundImageUpload,
    handleDeleteBackgroundImage,
    handleAddBackgroundImageUrl,
    handleAddSelectedAsBackgrounds,
  };
}
